import { useCallback, useEffect, useMemo, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import AdminOrchSplitPane from '@frontend/components/user-access/AdminOrchSplitPane';
import {
	BUG_LOCATIONS,
	FEATURE_LOCATIONS,
	FEATURE_STORY_PLACEHOLDER,
} from '@frontend/pages/dashboard/feedbackLocations';
import {
	DEFAULT_FEEDBACK_PRIORITY,
	FEEDBACK_PRIORITIES,
	feedbackPriorityLabel,
	feedbackPriorityOptionLabel,
	type FeedbackPriority,
} from '@frontend/pages/dashboard/feedbackPriorities';

interface FeedbackSubmission {
	id: string;
	kind: 'bug' | 'feature';
	subject: string;
	location?: string;
	priority?: string;
	status: string;
	workPackageId: number | null;
	href: string;
	createdAt: string;
}

type SimilarMatch = FeedbackSubmission;

async function feedbackFetch<T>(
	path: string,
	init?: RequestInit
): Promise<T & { message?: string; matches?: SimilarMatch[] }> {
	const token = authClient.getToken();
	const response = await fetch(path, {
		...init,
		headers: {
			'Content-Type': 'application/json',
			...(token ? { Authorization: `Bearer ${token}` } : {}),
			...(init?.headers || {}),
		},
	});
	const data = (await response.json()) as { message?: string; matches?: SimilarMatch[] } & T;
	if (!response.ok) {
		const err = new Error(data.message || `Request failed (${response.status})`) as Error & {
			status?: number;
			matches?: SimilarMatch[];
		};
		err.status = response.status;
		err.matches = data.matches;
		throw err;
	}
	return data;
}

export default function FeedbackDetail() {
	const [tab, setTab] = useState<'bug' | 'feature'>('bug');
	const [reporterLabel, setReporterLabel] = useState('');

	const [bugSubject, setBugSubject] = useState('');
	const [bugLocation, setBugLocation] = useState(BUG_LOCATIONS[0]);
	const [bugPriority, setBugPriority] = useState<FeedbackPriority>(DEFAULT_FEEDBACK_PRIORITY);
	const [bugSteps, setBugSteps] = useState('');
	const [bugNotes, setBugNotes] = useState('');
	const [bugContactNote, setBugContactNote] = useState('');
	const [bugNotify, setBugNotify] = useState(true);

	const [featureSubject, setFeatureSubject] = useState('');
	const [featureLocation, setFeatureLocation] = useState(FEATURE_LOCATIONS[0]);
	const [featurePriority, setFeaturePriority] = useState<FeedbackPriority>(DEFAULT_FEEDBACK_PRIORITY);
	const [featureStory, setFeatureStory] = useState('');
	const [featureNotes, setFeatureNotes] = useState('');
	const [featureContactNote, setFeatureContactNote] = useState('');
	const [featureNotify, setFeatureNotify] = useState(true);

	const [submissions, setSubmissions] = useState<FeedbackSubmission[]>([]);
	const [openItems, setOpenItems] = useState<FeedbackSubmission[]>([]);
	const [openFilter, setOpenFilter] = useState('');
	const [similar, setSimilar] = useState<SimilarMatch[]>([]);
	const [acknowledgeDuplicates, setAcknowledgeDuplicates] = useState(false);
	const [configured, setConfigured] = useState(true);
	const [available, setAvailable] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	const [message, setMessage] = useState('');

	const activeLocation = tab === 'bug' ? bugLocation : featureLocation;
	const activeSubject = tab === 'bug' ? bugSubject : featureSubject;

	const similarIds = useMemo(() => new Set(similar.map((s) => s.id)), [similar]);

	const reloadMine = useCallback(async () => {
		const path = tab === 'bug' ? '/api/feedback/bugs' : '/api/feedback/features';
		const data = await feedbackFetch<{
			available: boolean;
			configured?: boolean;
			submissions: FeedbackSubmission[];
			message?: string;
		}>(path);
		setAvailable(data.available);
		setConfigured(data.configured !== false);
		setSubmissions(data.submissions || []);
		if (data.message) setMessage(data.message);
	}, [tab]);

	const reloadOpen = useCallback(
		async (location: string, q: string) => {
			const params = new URLSearchParams({ kind: tab, location });
			if (q.trim()) params.set('q', q.trim());
			const data = await feedbackFetch<{ items: FeedbackSubmission[] }>(
				`/api/feedback/open?${params}`
			);
			setOpenItems(data.items || []);
		},
		[tab]
	);

	useEffect(() => {
		authClient
			.getCurrentUser()
			.then((user) => {
				if (!user) return;
				const parts = [user.username, user.email].filter(Boolean);
				setReporterLabel(parts.join(' · ') || 'Signed-in user');
			})
			.catch(() => setReporterLabel('Signed-in user'));
	}, []);

	useEffect(() => {
		setSimilar([]);
		setAcknowledgeDuplicates(false);
		setOpenFilter('');
		setError('');
		reloadMine().catch((err: Error) => setError(err.message));
	}, [reloadMine]);

	useEffect(() => {
		const handle = window.setTimeout(() => {
			reloadOpen(activeLocation, openFilter).catch(() => setOpenItems([]));
		}, 300);
		return () => window.clearTimeout(handle);
	}, [activeLocation, openFilter, reloadOpen]);

	useEffect(() => {
		if (activeSubject.trim().length < 3) {
			setSimilar([]);
			setAcknowledgeDuplicates(false);
			return;
		}
		const handle = window.setTimeout(() => {
			const q = new URLSearchParams({ kind: tab, subject: activeSubject.trim() });
			feedbackFetch<{ matches: SimilarMatch[] }>(`/api/feedback/similar?${q}`)
				.then((data) => {
					setSimilar(data.matches || []);
					if (!(data.matches || []).length) setAcknowledgeDuplicates(false);
				})
				.catch(() => {
					/* ignore while typing */
				});
		}, 400);
		return () => window.clearTimeout(handle);
	}, [tab, activeSubject]);

	const handleSubmit = async () => {
		setBusy(true);
		setError('');
		setMessage('');
		try {
			if (tab === 'bug') {
				await feedbackFetch('/api/feedback/bugs', {
					method: 'POST',
					body: JSON.stringify({
						subject: bugSubject.trim(),
						location: bugLocation,
						priority: bugPriority,
						stepsToReproduce: bugSteps.trim(),
						notes: bugNotes.trim(),
						contactNote: bugContactNote.trim(),
						notifyOnUpdate: bugNotify,
						acknowledgeDuplicates,
					}),
				});
				setBugSubject('');
				setBugPriority(DEFAULT_FEEDBACK_PRIORITY);
				setBugSteps('');
				setBugNotes('');
				setBugContactNote('');
				setBugNotify(true);
			} else {
				await feedbackFetch('/api/feedback/features', {
					method: 'POST',
					body: JSON.stringify({
						subject: featureSubject.trim(),
						location: featureLocation,
						priority: featurePriority,
						storyBody: featureStory.trim(),
						notes: featureNotes.trim(),
						contactNote: featureContactNote.trim(),
						notifyOnUpdate: featureNotify,
						acknowledgeDuplicates,
					}),
				});
				setFeatureSubject('');
				setFeaturePriority(DEFAULT_FEEDBACK_PRIORITY);
				setFeatureStory('');
				setFeatureNotes('');
				setFeatureContactNote('');
				setFeatureNotify(true);
			}
			setSimilar([]);
			setAcknowledgeDuplicates(false);
			setMessage(
				tab === 'bug'
					? 'Thanks — your bug report was filed in the platform OpenProject project.'
					: 'Thanks — your feature request was filed in the platform OpenProject project.'
			);
			await reloadMine();
			await reloadOpen(activeLocation, openFilter);
		} catch (err) {
			const e = err as Error & { status?: number; matches?: SimilarMatch[] };
			if (e.status === 409 && e.matches?.length) {
				setSimilar(e.matches);
				setError(e.message);
			} else {
				setError(e.message);
			}
		} finally {
			setBusy(false);
		}
	};

	const bugReady =
		Boolean(bugSubject.trim()) && Boolean(bugLocation) && Boolean(bugSteps.trim());
	const featureReady =
		Boolean(featureSubject.trim()) && Boolean(featureLocation) && Boolean(featureStory.trim());
	const canSubmit =
		!busy &&
		(tab === 'bug' ? bugReady : featureReady) &&
		(similar.length === 0 || acknowledgeDuplicates);

	return (
		<div className="tree-dashboard-panel feedback-panel">
			<h2>Bug report / Feature request</h2>
			<p className="tree-dashboard-muted">
				Report bugs or request features for the Patriotic Projects platform. Submissions are filed in
				the platform OpenProject project. Review open items on the right before filing a duplicate.
			</p>

			{error ? <div className="settings-message settings-error">{error}</div> : null}
			{message ? <div className="settings-message settings-success">{message}</div> : null}

			{!available ? (
				<div className="tree-dashboard-callout">
					Platform feedback requires PostgreSQL. Ask a system admin to enable the portal database.
				</div>
			) : !configured ? (
				<div className="tree-dashboard-callout">
					Platform feedback is not configured yet. Ask a system admin to set the OpenProject project
					identifier in portal data/platform-feedback.json.
				</div>
			) : (
				<>
					<div className="feedback-tabs" role="tablist" aria-label="Feedback type">
						<button
							type="button"
							role="tab"
							aria-selected={tab === 'bug'}
							className={`feedback-tab ${tab === 'bug' ? 'active' : ''}`}
							onClick={() => setTab('bug')}
						>
							Bug report
						</button>
						<button
							type="button"
							role="tab"
							aria-selected={tab === 'feature'}
							className={`feedback-tab ${tab === 'feature' ? 'active' : ''}`}
							onClick={() => setTab('feature')}
						>
							Feature request
						</button>
					</div>

					<AdminOrchSplitPane
						className="feedback-compose-split admin-orch-master-detail"
						defaultLeftWidth="50%"
						resizeLabel="Resize bug report form and open items list"
						left={
						<div className="feedback-form">
							<div className="ai-story-field-row">
								<span className="ai-story-field-label">Reporter</span>
								<p className="feedback-reporter">{reporterLabel || 'Signed-in user'}</p>
							</div>

							{tab === 'bug' ? (
								<>
									<div className="ai-story-field-row">
										<label htmlFor="bug-subject">Subject</label>
										<input
											id="bug-subject"
											value={bugSubject}
											onChange={(e) => setBugSubject(e.target.value)}
											maxLength={200}
											placeholder="Short summary of the bug"
										/>
									</div>
									<div className="ai-story-field-row">
										<label htmlFor="bug-location">Location in the platform</label>
										<select
											id="bug-location"
											value={bugLocation}
											onChange={(e) => setBugLocation(e.target.value)}
										>
											{BUG_LOCATIONS.map((loc) => (
												<option key={loc} value={loc}>
													{loc}
												</option>
											))}
										</select>
									</div>
									<div className="ai-story-field-row">
										<label htmlFor="bug-priority">Priority</label>
										<select
											id="bug-priority"
											value={bugPriority}
											onChange={(e) => setBugPriority(e.target.value as FeedbackPriority)}
										>
											{FEEDBACK_PRIORITIES.map((p) => (
												<option key={p.value} value={p.value}>
													{feedbackPriorityOptionLabel(p)}
												</option>
											))}
										</select>
									</div>
									<div className="ai-story-field-row">
										<label htmlFor="bug-steps">Steps to reproduce</label>
										<textarea
											id="bug-steps"
											rows={6}
											value={bugSteps}
											onChange={(e) => setBugSteps(e.target.value)}
											maxLength={8000}
											placeholder={'1. Go to …\n2. Click …\n3. Observe …'}
										/>
									</div>
									<div className="ai-story-field-row">
										<label htmlFor="bug-notes">Notes (optional)</label>
										<textarea
											id="bug-notes"
											rows={3}
											value={bugNotes}
											onChange={(e) => setBugNotes(e.target.value)}
											maxLength={4000}
											placeholder="Expected vs actual, browser, screenshots notes…"
										/>
									</div>
									<div className="ai-story-field-row">
										<label htmlFor="bug-contact">Additional contact note (optional)</label>
										<input
											id="bug-contact"
											value={bugContactNote}
											onChange={(e) => setBugContactNote(e.target.value)}
											maxLength={500}
											placeholder="Preferred contact if different from account email"
										/>
									</div>
									<label className="feedback-checkbox">
										<input
											type="checkbox"
											checked={bugNotify}
											onChange={(e) => setBugNotify(e.target.checked)}
										/>
										Notify me when this issue is updated or fixed
									</label>
								</>
							) : (
								<>
									<div className="ai-story-field-row">
										<label htmlFor="feature-subject">Subject</label>
										<input
											id="feature-subject"
											value={featureSubject}
											onChange={(e) => setFeatureSubject(e.target.value)}
											maxLength={200}
											placeholder="Short summary of the capability"
										/>
									</div>
									<div className="ai-story-field-row">
										<label htmlFor="feature-location">Location in the platform</label>
										<select
											id="feature-location"
											value={featureLocation}
											onChange={(e) => setFeatureLocation(e.target.value)}
										>
											{FEATURE_LOCATIONS.map((loc) => (
												<option key={loc} value={loc}>
													{loc}
												</option>
											))}
										</select>
									</div>
									<div className="ai-story-field-row">
										<label htmlFor="feature-priority">Priority</label>
										<select
											id="feature-priority"
											value={featurePriority}
											onChange={(e) => setFeaturePriority(e.target.value as FeedbackPriority)}
										>
											{FEEDBACK_PRIORITIES.map((p) => (
												<option key={p.value} value={p.value}>
													{feedbackPriorityOptionLabel(p)}
												</option>
											))}
										</select>
									</div>
									<div className="ai-story-field-row">
										<label htmlFor="feature-story">User story</label>
										<p className="tree-dashboard-muted feedback-hint">
											Use the Connextra format: As a …, I want …, so that …. Optionally add Given /
											When / Then acceptance notes.
										</p>
										<textarea
											id="feature-story"
											rows={8}
											value={featureStory}
											onChange={(e) => setFeatureStory(e.target.value)}
											maxLength={8000}
											placeholder={FEATURE_STORY_PLACEHOLDER}
										/>
									</div>
									<div className="ai-story-field-row">
										<label htmlFor="feature-notes">Notes (optional)</label>
										<textarea
											id="feature-notes"
											rows={3}
											value={featureNotes}
											onChange={(e) => setFeatureNotes(e.target.value)}
											maxLength={4000}
											placeholder="Extra context, constraints, related work…"
										/>
									</div>
									<div className="ai-story-field-row">
										<label htmlFor="feature-contact">Additional contact note (optional)</label>
										<input
											id="feature-contact"
											value={featureContactNote}
											onChange={(e) => setFeatureContactNote(e.target.value)}
											maxLength={500}
											placeholder="Preferred contact if different from account email"
										/>
									</div>
									<label className="feedback-checkbox">
										<input
											type="checkbox"
											checked={featureNotify}
											onChange={(e) => setFeatureNotify(e.target.checked)}
										/>
										Notify me when this request is updated
									</label>
								</>
							)}

							{similar.length > 0 ? (
								<div className="feedback-dup-note">
									<p>
										Subject looks similar to {similar.length} open item
										{similar.length === 1 ? '' : 's'} (highlighted on the right). Review them, then
										confirm if this is still distinct.
									</p>
									<label className="feedback-checkbox">
										<input
											type="checkbox"
											checked={acknowledgeDuplicates}
											onChange={(e) => setAcknowledgeDuplicates(e.target.checked)}
										/>
										Submit anyway — this is not a duplicate
									</label>
								</div>
							) : null}

							<button
								type="button"
								className="btn btn-primary"
								disabled={!canSubmit}
								onClick={handleSubmit}
							>
								{busy
									? 'Submitting…'
									: tab === 'bug'
										? 'Submit bug report'
										: 'Submit feature request'}
							</button>
						</div>
						}
						right={
						<aside className="feedback-open-panel" aria-label={tab === 'bug' ? 'Open bugs' : 'Open features'}>
							<h3>{tab === 'bug' ? 'Open bug reports' : 'Open feature requests'}</h3>
							<p className="tree-dashboard-muted feedback-hint">
								Filtered by location: <strong>{activeLocation}</strong>
							</p>
							<input
								className="feedback-open-filter"
								type="search"
								value={openFilter}
								onChange={(e) => setOpenFilter(e.target.value)}
								placeholder="Filter by subject…"
								aria-label="Filter open items by subject"
							/>
							{openItems.length === 0 ? (
								<p className="tree-dashboard-muted">No open items for this location.</p>
							) : (
								<ul className="feedback-open-list">
									{openItems.map((item) => (
										<li
											key={item.id}
											className={similarIds.has(item.id) ? 'feedback-open-match' : undefined}
										>
											<div className="widget-list-primary">
												{item.href ? (
													<a href={item.href} target="_blank" rel="noopener noreferrer">
														{item.subject}
													</a>
												) : (
													<span>{item.subject}</span>
												)}
											</div>
											<div className="widget-list-meta">
												{item.priority ? (
													<span>{feedbackPriorityLabel(item.priority)}</span>
												) : null}
												<span>{item.status}</span>
											</div>
										</li>
									))}
								</ul>
							)}
						</aside>
						}
					/>
				</>
			)}

			<section className="feedback-history">
				<h3>{tab === 'bug' ? 'Your recent bug reports' : 'Your recent feature requests'}</h3>
				{submissions.length === 0 ? (
					<p className="tree-dashboard-muted">No submissions yet.</p>
				) : (
					<ul className="widget-list">
						{submissions.map((item) => (
							<li key={item.id} className="widget-list-item">
								<div className="widget-list-primary">
									{item.href ? (
										<a href={item.href} target="_blank" rel="noopener noreferrer">
											{item.subject}
										</a>
									) : (
										<span>{item.subject}</span>
									)}
								</div>
								<div className="widget-list-meta">
									{item.location ? <span>{item.location}</span> : null}
									{item.priority ? <span>{feedbackPriorityLabel(item.priority)}</span> : null}
									<span>{item.status}</span>
									<span>{new Date(item.createdAt).toLocaleDateString()}</span>
								</div>
							</li>
						))}
					</ul>
				)}
			</section>
		</div>
	);
}
